'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { ApiReferenceDesktopNavigation } from '@features/playground.containers/ApiReferenceNavigation/ApiReferenceDesktopNavigation'
import { ApiReferenceMobileNavigation } from '@features/playground.containers/ApiReferenceNavigation/ApiReferenceMobileNavigation'
import { usePathname, useRouter } from 'next/navigation'

import { transformGroupsToNavigationArray } from './helpers'

import type { NavigationItem } from '@features/navigation.components/Navigation/types'
import type {
  SdkOperation,
  SdkOperationGroupInfoFragment,
} from '@features/sdk-explorer.graphql'

export interface SdkExplorerNavigationProps {
  /**
   * An array of additional navigation items to be displayed.
   */
  additionalNavigationItems?: NavigationItem[]
  /**
   * The SDK operation to be displayed.
   */
  operation?: SdkOperation
  /**
   * An array of SDK operation group information fragments.
   */
  operationGroups?: SdkOperationGroupInfoFragment[]
  /**
   * The programming language for which the SDK reference is being displayed.
   */
  language: string
  /**
   * The version of the SDK being referenced.
   */
  version?: string
  /**
   * Slug represents the url of the current operation.
   */
  slug: string
  /**
   * Label for navigation back button.
   */
  backButtonLabel: string
}

export const SdkExplorerNavigation = ({
  additionalNavigationItems,
  operationGroups,
  operation,
  language,
  version,
  slug,
  backButtonLabel,
}: SdkExplorerNavigationProps) => {
  const [currentScope, setCurrentScope] = useState<string | null>(null)

  const router = useRouter()

  const [query, setQuery] = useState('')
  const pathname = usePathname()
  const prefix = 'sdk-explorer'

  const selectedOperation = useMemo(() => {
    return {
      ...operation,
      slug,
    }
  }, [slug, operation])

  const operationGroupData = useMemo(
    () =>
      transformGroupsToNavigationArray(
        operationGroups || [],
        language,
        version,
      ),
    [language, operationGroups, version],
  )

  /**
   * Handles the change of the current scope.
   */
  const handleScopeChange = useCallback(
    (item: string) => {
      // Navigate to the getting-started page for the selected scope
      router.push(`/sdk-explorer/${item}/${language}/getting-started`)
    },
    [language, router],
  )

  /**
   * Sets the default scope based on the current path. If the current path does not contain a scope, the first scope is selected.
   */
  useEffect(() => {
    const scopePerPath = pathname.split('/')[2]
    if (operationGroupData.length > 0 && currentScope === null) {
      if (scopePerPath) {
        setCurrentScope(scopePerPath)
      } else {
        setCurrentScope(operationGroupData[0].key)
      }
    }
  }, [operationGroupData, currentScope, pathname])

  const props = {
    additionalNavigationItems: additionalNavigationItems,
    backButtonLabel: backButtonLabel,
    currentScope: currentScope,
    fallbackBackUrl: '/sdk-explorer',
    handleScopeChange: handleScopeChange,
    operationGroups: operationGroupData,
    prefix: prefix,
    query: query,
    selectedOperation: selectedOperation,
    setQuery: setQuery,
    link: true,
  }

  return (
    <>
      <ApiReferenceDesktopNavigation {...props} />
      <ApiReferenceMobileNavigation {...props} />
    </>
  )
}
